import {inject, injectable} from 'inversify';
import {makeAutoObservable, runInAction} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {CoreDiaryEntryApiModel} from '../../../../../api/types/core/models';
import type {MetricModel} from '../../../../../core/domain/Metric/Metric.model';
import type {UserModel} from '../../../../../core/domain/User/User.model';
import type {Period} from '../../../types';
import type {
  TrackersAction,
  TrackersActionsContext,
  VisibleWidgets,
} from './types';

import HideSvg from '../../../../../assets/icons/primary/Hide.svg';
import ShowSvg from '../../../../../assets/icons/primary/Show.svg';
import {alert} from '../../../../../components/alert';
import {DateTimeModelFactory} from '../../../../../core/domain/Date/DateTime.factory';
import {DateTimeModel} from '../../../../../core/domain/Date/DateTime.model';
import {CurrentUserService} from '../../../../../core/system/currentUser/CurrentUser.service';
import {type Timestamp} from '../../../../../utils/datetime';
import {
  AsyncBusyManager,
  type Destructible,
  type WithBusyManager,
} from '../../../../../utils/store';
import {CreateDiaryEntryUseCase} from '../../useCases/CreateDiaryEntry.uc';
import {DeleteDiaryEntryUseCase} from '../../useCases/DeleteDiaryEntry.uc';
import {FetchMetricsUseCase} from '../../useCases/FetchMetrics.uc';
import {ListDiaryEntriesUseCase} from '../../useCases/ListDiaryEntries.uc';
import {TrackWeightUseCase} from '../../useCases/TrackWeight.uc';

@injectable()
export class TrackersStore
  implements Destructible, WithBusyManager<TrackersStore.BusyKey>
{
  busy = new AsyncBusyManager<TrackersStore.BusyKey>();
  diaryEntries: CoreDiaryEntryApiModel[] = [];
  selectedDate: DateTimeModel;
  selectedPeriod: Period = 'week';
  selectedUnit: MetricModel.WeightUnit;
  private DATE_FORMAT = 'YYYY-MM-DD';
  private metricsByDate = new Map<string, MetricModel>();

  constructor(
    @inject(DateTimeModelFactory)
    private readonly dateTimeFactory: DateTimeModelFactory,
    @inject(TrackWeightUseCase)
    private readonly trackWeightUseCase: TrackWeightUseCase,
    @inject(FetchMetricsUseCase)
    private readonly fetchMetrics: FetchMetricsUseCase,
    @inject(CreateDiaryEntryUseCase)
    private readonly createDiaryEntryUseCase: CreateDiaryEntryUseCase,
    @inject(ListDiaryEntriesUseCase)
    private readonly listDiaryEntriesUseCase: ListDiaryEntriesUseCase,
    @inject(DeleteDiaryEntryUseCase)
    private readonly deleteDiaryEntryUseCase: DeleteDiaryEntryUseCase,
    @inject(CurrentUserService)
    private readonly userService: CurrentUserService,
  ) {
    this.selectedUnit = this.getWeightUnit();
    this.selectedDate = dateTimeFactory.now();

    this.busy.observe(this, {
      fetch: 'fetch',
      setSelectedDate: 'setSelectedDate',
    });

    makeAutoObservable(this);
  }

  init() {
    this.fetch();
    this.fetchDiaryEntries();
  }

  destruct() {
    this.busy.destruct();
  }

  get currentWeight() {
    const currentMetric = this.metricsByDate.get(
      this.selectedDate.format(this.DATE_FORMAT),
    );

    if (!currentMetric) {
      return null;
    }

    return {
      unit: this.selectedUnit,
      weight: currentMetric.getByUnit(this.selectedUnit),
    };
  }

  get isCurrentWeightBlockVisible(): boolean {
    return this.isWidgetVisible('current_weight');
  }

  get isFetching() {
    return this.busy.anyReason;
  }

  get isFetchingByDate() {
    return this.busy.getReason('setSelectedDate');
  }

  get isTodayEmpty() {
    return !this.isFetching && !this.currentWeight;
  }

  get isWeightHistoryBlockVisible(): boolean {
    return this.isWidgetVisible('weight_history');
  }

  get metrics() {
    return Array.from(this.metricsByDate.values());
  }

  get periodOptions() {
    const periods: Period[] = ['week', 'month', 'half-year', 'year'];

    return periods.map(period => ({
      isSelected: period === this.selectedPeriod,
      /**
       * @i18n_parser
       *
       * t('shared:period_labels.week')
       * t('shared:period_labels.month')
       * t('shared:period_labels.half-year')
       * t('shared:period_labels.year')
       */
      label: i18n.t(`shared:period_labels.${period}`).toUpperCase(),
      value: period,
    }));
  }

  get weightUnitOptions() {
    const units: MetricModel.WeightUnit[] = ['lbs', 'kg'];

    return units.map(unit => ({
      isSelected: unit === this.selectedUnit,
      /**
       * @i18n_parser
       *
       * t('shared:lbs')
       * t('shared:kg')
       */
      label: i18n.t(`shared:${unit}`),
      value: unit,
    }));
  }

  async createDiaryEntry(props: CreateDiaryEntryUseCase.Props) {
    try {
      const entry = await this.createDiaryEntryUseCase.exec(props);

      runInAction(() => {
        this.diaryEntries.unshift(entry);
      });

      return entry;
    } catch (e) {
      alert.error(e);
    }
  }

  async deleteDiaryEntry(id: string) {
    try {
      await this.deleteDiaryEntryUseCase.exec({id});

      runInAction(() => {
        this.diaryEntries = this.diaryEntries.filter(e => e._id !== id);
      });

      return true;
    } catch (e) {
      alert.error(e);
    }
  }

  async fetch() {
    try {
      const metrics = await this.fetchMetrics.exec({
        endDate: this.selectedDate,
        period: this.selectedPeriod,
      });

      runInAction(() => {
        this.metricsByDate.clear();
        metrics.forEach(metric => {
          this.metricsByDate.set(metric.date.format(this.DATE_FORMAT), metric);
        });
      });
    } catch (e) {
      alert.error(e);
    }
  }

  async fetchDiaryEntries() {
    try {
      const diaryEntries = await this.listDiaryEntriesUseCase.exec({
        query: [['type', '==', 'weight_tracker']],
        sort: [['created', -1]],
      });

      runInAction(() => {
        this.diaryEntries = diaryEntries;
      });
    } catch (e) {
      alert.error(e);
    }
  }

  getTrackersActions(context: TrackersActionsContext): TrackersAction[] {
    const key = this.contextToWidgetKey(context);
    const isVisible = this.isWidgetVisible(key);
    const actions: TrackersAction[] = [];

    if (isVisible) {
      actions.push({
        exec: () => this.setWidgetVisibility(context, false),
        icon: HideSvg,
        id: 'hide',
        /**
         * @i18n_parser
         * t('shared:you_can_hide_this_block')
         * t('shared:hide')
         */
        text: i18n.t('shared:you_can_hide_this_block'),
        title: i18n.t('shared:hide'),
      });
    } else {
      actions.push({
        exec: () => this.setWidgetVisibility(context, true),
        icon: ShowSvg,
        id: 'show',
        /**
         * @i18n_parser
         * t('shared:make_this_block_visible_again')
         * t('shared:show')
         */
        text: i18n.t('shared:make_this_block_visible_again'),
        title: i18n.t('shared:show'),
      });
    }

    return actions;
  }

  async setSelectedDate(date: Timestamp) {
    this.selectedDate = this.dateTimeFactory.create(date);

    await this.fetch();
  }

  async setSelectedPeriod(period: Period) {
    this.selectedPeriod = period;

    await this.fetch();
  }

  setSelectedUnit(unit: MetricModel.WeightUnit) {
    this.selectedUnit = unit;

    this.updateUserWeightUnit(unit);
  }

  async setWidgetVisibility(
    context: TrackersActionsContext,
    visible: boolean,
  ): Promise<void> {
    const key = this.contextToWidgetKey(context);
    const currentUser = this.userService.user;

    if (!currentUser) return;

    try {
      const prev = this.getVisibleWidgets();
      const next = {...prev, [key]: visible};

      const visible_widgets = (
        Object.entries(next) as [UserModel.VisibleWidgetsKey, boolean][]
      )
        .filter(([, isVisible]) => isVisible)
        .map(([widgetKey]) => widgetKey);

      currentUser.update({visible_widgets});
      await this.userService.update(currentUser);
    } catch (e) {
      alert.error(e);
    }
  }

  async trackWeight({
    unit,
    weight,
  }: {
    unit: MetricModel.WeightUnit;
    weight: number;
  }) {
    // We are handling error in AddWeightModal
    const metric = await this.trackWeightUseCase.exec({
      date: this.selectedDate,
      unit,
      weight,
    });

    runInAction(() => {
      this.metricsByDate.set(metric.date.format(this.DATE_FORMAT), metric);

      if (this.selectedUnit !== unit) {
        this.setSelectedUnit(unit);
      }
    });
  }

  private contextToWidgetKey(
    context: TrackersActionsContext,
  ): UserModel.VisibleWidgetsKey {
    return context === 'reporting' ? 'weight_history' : 'current_weight';
  }

  private getVisibleWidgets(): VisibleWidgets {
    const list = this.userService.user?.visible_widgets;

    if (Array.isArray(list)) {
      const isInList = (key: UserModel.VisibleWidgetsKey) => list.includes(key);

      return {
        current_weight: isInList('current_weight'),
        weight_history: isInList('weight_history'),
      };
    }

    return {current_weight: true, weight_history: true};
  }

  private getWeightUnit(): MetricModel.WeightUnit {
    const savedUnit = this.userService.user?.metadata.settings?.weight_unit;

    if (savedUnit) {
      return savedUnit;
    }

    return 'lbs';
  }

  private isWidgetVisible(key: UserModel.VisibleWidgetsKey): boolean {
    const v = this.getVisibleWidgets();

    return v[key] === true;
  }

  private updateUserWeightUnit(unit: MetricModel.WeightUnit) {
    const currentUser = this.userService.user;

    if (!currentUser || currentUser.metadata.settings?.weight_unit === unit) {
      return;
    }

    const prevMetadata = currentUser.metadata ?? {};
    const prevSettings =
      (prevMetadata as {settings?: {weight_unit?: MetricModel.WeightUnit}})
        .settings ?? {};

    currentUser.update({
      metadata: {
        ...prevMetadata,
        settings: {
          ...prevSettings,
          weight_unit: unit,
        },
      },
    });

    this.userService.update(currentUser);
  }
}

export namespace TrackersStore {
  export type BusyKey = 'fetch' | 'setSelectedDate';
}
